import React from "react";
import { Alert, Tag, Descriptions } from "antd";
import { format, fromUnixTime } from "date-fns";

export type ReferrerNote = { code: string; message: string };

export type ReferrerUserSummary = {
    userId: number;
    name: string | null;
    email: string | null;
    createTime: number | null;
    ref: number | null;
    verified: boolean | null;
    active: boolean | null;
};

export type ReferrerPreview = {
    user: ReferrerUserSummary;
    currentRef: ReferrerUserSummary | null;
    referrer: (ReferrerUserSummary & { completedOrders: number; eligible: boolean; minOrders: number }) | null;
    window: { deadline: number | null; isExpired: boolean; daysLeft: number; rewardMonths: number };
    blockers: ReferrerNote[];
    warnings: ReferrerNote[];
    backfill: {
        unpaid: { orders: number; amount: number };
        paid: { orders: number; amount: number };
        detach: { orders: number };
        truncated: boolean;
    } | null;
};

const vnd = (v: number) => (v || 0).toLocaleString("vi-VN");

const nameOf = (u: ReferrerUserSummary | null) =>
    u ? `#${u.userId} · ${u.name || "(chưa đặt tên)"} · ${u.email || "—"}` : "—";

/**
 * Khối xem trước tác động của thao tác gán/đổi/gỡ người giới thiệu.
 * Hiển thị hạn 3 tháng, điều kiện bị chặn, cảnh báo, và phạm vi backfill đơn cũ.
 */
function ReferrerPreviewPanel({ preview }: { preview: ReferrerPreview }) {
    const { window: win, backfill } = preview;

    return (
        <div className="flex flex-col gap-3">
            <Descriptions size="small" column={1} bordered>
                <Descriptions.Item label="Người được giới thiệu">{nameOf(preview.user)}</Descriptions.Item>
                <Descriptions.Item label="Người giới thiệu hiện tại">
                    {preview.currentRef ? nameOf(preview.currentRef) : <span className="text-content-3">Chưa có</span>}
                </Descriptions.Item>
                <Descriptions.Item label={`Hạn hưởng hoa hồng (${win.rewardMonths} tháng)`}>
                    {win.deadline ? (
                        <span>
                            {format(fromUnixTime(win.deadline), "dd/MM/yyyy")}{" "}
                            {win.isExpired ? (
                                <Tag color="red">Đã hết hạn</Tag>
                            ) : (
                                <Tag color="green">Còn {win.daysLeft} ngày</Tag>
                            )}
                        </span>
                    ) : (
                        <Tag color="red">Không xác định</Tag>
                    )}
                </Descriptions.Item>
                {preview.referrer && (
                    <Descriptions.Item label="Người giới thiệu mới">
                        <div className="flex flex-col gap-1">
                            <span>{nameOf(preview.referrer)}</span>
                            <span>
                                {preview.referrer.eligible ? (
                                    <Tag color="green">Đủ điều kiện ({preview.referrer.completedOrders} đơn hoàn tất)</Tag>
                                ) : (
                                    <Tag color="orange">
                                        {preview.referrer.completedOrders}/{preview.referrer.minOrders} đơn hoàn tất
                                    </Tag>
                                )}
                            </span>
                        </div>
                    </Descriptions.Item>
                )}
            </Descriptions>

            {preview.blockers.map((b) => (
                <Alert key={b.code} type="error" showIcon message={b.message} />
            ))}
            {preview.warnings.map((w) => (
                <Alert key={w.code} type="warning" showIcon message={w.message} />
            ))}

            {backfill && (
                <Alert
                    type="info"
                    showIcon
                    message="Phạm vi áp dụng cho đơn cũ"
                    description={
                        <ul className="list-disc pl-4 m-0 text-sm">
                            <li>
                                Đơn <b>chưa thanh toán</b> trong hạn: <b>{backfill.unpaid.orders}</b> đơn ·{" "}
                                <b>{vnd(backfill.unpaid.amount)}đ</b> — tiền do cron trả như bình thường.
                            </li>
                            <li>
                                Đơn <b>đã thanh toán</b> chưa từng tính hoa hồng: <b>{backfill.paid.orders}</b> đơn ·{" "}
                                <b>{vnd(backfill.paid.amount)}đ</b> — chỉ chạy ở mức &quot;Cả đơn đã thanh toán&quot;,
                                cộng thẳng vào ví người giới thiệu.
                            </li>
                            {backfill.detach.orders > 0 && (
                                <li className="text-orange-600">
                                    {backfill.detach.orders} đơn chưa thanh toán đang gắn người giới thiệu cũ sẽ bị gỡ ở
                                    mọi mức (tiền chưa trả nên không thu hồi gì).
                                </li>
                            )}
                            {backfill.truncated && (
                                <li className="text-orange-600">
                                    User có quá nhiều đơn — chỉ quét phần gần nhất, số liệu trên là chưa đầy đủ.
                                </li>
                            )}
                        </ul>
                    }
                />
            )}
        </div>
    );
}

export default ReferrerPreviewPanel;
